import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  PayrollState, 
  PayrollSession, 
  MonthlyPayrollData, 
  AnnualTotals,
  DataSourceType,
  MonthlyIncomeData,
  SalarySlipData
} from '../types/PayrollTypes';
import { PayrollStorage, ensureFamilyData } from '../utils/payrollStorage';
import { PayrollEstimation } from '../utils/payrollEstimation';
import type { FamilyFormData } from '../domain/tax/types';

/**
 * セッションIDを採番する。
 * `crypto.randomUUID` が使える環境ではそれを利用し、無ければ時刻と乱数から生成する。
 */
function createSessionId(): string {
  const webCrypto = typeof window !== 'undefined' ? window.crypto : undefined;
  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }
  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

interface PayrollContextType {
  payrollState: PayrollState;
  initializeSession: (year?: number) => void;
  addPdfData: (pdfData: SalarySlipData) => void;
  addBonus: (bonusData: MonthlyPayrollData) => void;
  deleteBonus: (year: number, month: number) => void;
  updateMonthlyData: (year: number, month: number, isBonus: boolean, data: Partial<MonthlyPayrollData>) => void;
  updateFamilyData: (form: FamilyFormData) => void;
  calculateAnnualTotals: () => AnnualTotals;
  getMonthlyIncomeData: () => MonthlyIncomeData[];
  clearSession: () => void;
  resetMonthlyData: (year: number, month: number, isBonus: boolean) => void;
  resetAllUserEditedData: () => void;
  restoreSession: (restoredSession: PayrollSession) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  changeYear: (year: number, onCalculationReset?: () => void) => void;
}

const PayrollContext = createContext<PayrollContextType | undefined>(undefined);

export const usePayroll = () => {
  const context = useContext(PayrollContext);
  if (context === undefined) {
    throw new Error('usePayroll must be used within a PayrollProvider');
  }
  return context;
};

interface PayrollProviderProps {
  children: ReactNode;
}

export const PayrollProvider: React.FC<PayrollProviderProps> = ({ children }) => {
  const [payrollState, setPayrollState] = useState<PayrollState>({
    currentSession: null,
    isLoading: false,
    error: null
  });

  // コンポーネントマウント時にローカルストレージからセッションを復元
  useEffect(() => {
    try {
      const savedSession = PayrollStorage.loadSession();
      if (savedSession) {
        // 既存セッションにcurrentYearが未設定の場合は現在年を設定
        // 旧データ（familyData 無し）は既定値で補完する。
        const sessionToRestore = ensureFamilyData({
          ...savedSession,
          currentYear: savedSession.currentYear || new Date().getFullYear(),
          sessionId: savedSession.sessionId || createSessionId()
        });
        setPayrollState(prev => ({
          ...prev,
          currentSession: sessionToRestore
        }));
      }
    } catch (error) {
      console.error('セッション復元エラー:', error);
      setPayrollState(prev => ({
        ...prev,
        error: 'セッションの復元に失敗しました'
      }));
    }
  }, []);

  // セッション変更時の自動保存
  useEffect(() => {
    if (payrollState.currentSession) {
      PayrollStorage.autoSave(payrollState.currentSession);
    }
  }, [payrollState.currentSession]);

  const initializeSession = (year?: number) => {
    const currentYear = year || new Date().getFullYear();
    const newSession: PayrollSession = {
      currentYear,
      monthlyData: [],
      sessionId: createSessionId(),
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    };

    // 推定値で初期化（familyData は既定値で補完する）
    const sessionWithEstimations = PayrollEstimation.updateEstimations(ensureFamilyData(newSession));
    
    setPayrollState(prev => ({
      ...prev,
      currentSession: sessionWithEstimations,
      error: null
    }));
  };

  const addPdfData = (pdfData: SalarySlipData) => {
    if (!payrollState.currentSession) {
      throw new Error('セッションが初期化されていません');
    }

    try {
      // PDFデータから月別データを作成
      const newMonthlyData = PayrollEstimation.createMonthlyDataFromPdf(pdfData);

      // 既存データを更新または追加
      const updatedMonthlyData = [...payrollState.currentSession.monthlyData];
      const existingIndex = updatedMonthlyData.findIndex(data =>
        data.year === newMonthlyData.year &&
        data.month === newMonthlyData.month &&
        data.isBonus === newMonthlyData.isBonus
      );

      if (existingIndex >= 0) {
        updatedMonthlyData[existingIndex] = newMonthlyData;
      } else {
        updatedMonthlyData.push(newMonthlyData);
      }

      const updatedSession: PayrollSession = {
        ...payrollState.currentSession,
        monthlyData: updatedMonthlyData,
        lastUpdated: new Date().toISOString()
      };

      // 推定値を更新
      const sessionWithEstimations = PayrollEstimation.updateEstimations(updatedSession);

      setPayrollState(prev => ({
        ...prev,
        currentSession: sessionWithEstimations,
        error: null
      }));

    } catch (error) {
      console.error('PDFデータ追加エラー:', error);
      setPayrollState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'PDFデータの追加に失敗しました'
      }));
    }
  };

  const addBonus = (bonusData: MonthlyPayrollData) => {
    if (!payrollState.currentSession) {
      throw new Error('セッションが初期化されていません');
    }

    try {
      // 既存データを更新または追加
      const updatedMonthlyData = [...payrollState.currentSession.monthlyData];
      const existingIndex = updatedMonthlyData.findIndex(data =>
        data.year === bonusData.year &&
        data.month === bonusData.month &&
        data.isBonus === bonusData.isBonus
      );

      if (existingIndex >= 0) {
        updatedMonthlyData[existingIndex] = bonusData;
      } else {
        updatedMonthlyData.push(bonusData);
      }

      const updatedSession: PayrollSession = {
        ...payrollState.currentSession,
        monthlyData: updatedMonthlyData,
        lastUpdated: new Date().toISOString()
      };

      setPayrollState(prev => ({
        ...prev,
        currentSession: updatedSession,
        error: null
      }));

    } catch (error) {
      console.error('賞与データ追加エラー:', error);
      setPayrollState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '賞与データの追加に失敗しました'
      }));
    }
  };

  const deleteBonus = (year: number, month: number) => {
    if (!payrollState.currentSession) {
      throw new Error('セッションが初期化されていません');
    }

    try {
      const updatedMonthlyData = payrollState.currentSession.monthlyData.filter(data =>
        !(data.year === year && data.month === month && data.isBonus === true)
      );

      const updatedSession: PayrollSession = {
        ...payrollState.currentSession,
        monthlyData: updatedMonthlyData,
        lastUpdated: new Date().toISOString()
      };

      setPayrollState(prev => ({
        ...prev,
        currentSession: updatedSession,
        error: null
      }));

    } catch (error) {
      console.error('賞与データ削除エラー:', error);
      setPayrollState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '賞与データの削除に失敗しました'
      }));
    }
  };

  const updateMonthlyData = (
    year: number, 
    month: number, 
    isBonus: boolean, 
    data: Partial<MonthlyPayrollData>
  ) => {
    if (!payrollState.currentSession) {
      throw new Error('セッションが初期化されていません');
    }

    try {
      const updatedMonthlyData = [...payrollState.currentSession.monthlyData];
      const existingIndex = updatedMonthlyData.findIndex(item =>
        item.year === year && item.month === month && item.isBonus === isBonus
      );

      const updatedItem: MonthlyPayrollData = existingIndex >= 0 
        ? { ...updatedMonthlyData[existingIndex], ...data, lastUpdated: new Date().toISOString() }
        : {
            year,
            month,
            isBonus,
            grossSalary: 0,
            healthInsurance: 0,
            employmentInsurance: 0,
            nationalPension: 0,
            incomeTax: 0,
            localTax: 0,
            definedContribution: 0,
            sourceType: DataSourceType.USER_EDITED,
            lastUpdated: new Date().toISOString(),
            ...data
          };

      if (existingIndex >= 0) {
        updatedMonthlyData[existingIndex] = updatedItem;
      } else {
        updatedMonthlyData.push(updatedItem);
      }

      const updatedSession: PayrollSession = {
        ...payrollState.currentSession,
        monthlyData: updatedMonthlyData,
        lastUpdated: new Date().toISOString()
      };

      // ユーザー編集後に他の推定値を更新
      const sessionWithEstimations = PayrollEstimation.updateEstimations(updatedSession);

      setPayrollState(prev => ({
        ...prev,
        currentSession: sessionWithEstimations,
        error: null
      }));

    } catch (error) {
      console.error('月別データ更新エラー:', error);
      setPayrollState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '月別データの更新に失敗しました'
      }));
    }
  };

  // 扶養家族・控除の設定を更新する（session を単一の真実源とする）
  const updateFamilyData = (form: FamilyFormData) => {
    if (!payrollState.currentSession) {
      throw new Error('セッションが初期化されていません');
    }

    try {
      const updatedSession: PayrollSession = {
        ...payrollState.currentSession,
        familyData: form,
        lastUpdated: new Date().toISOString()
      };

      setPayrollState(prev => ({
        ...prev,
        currentSession: updatedSession,
        error: null
      }));

    } catch (error) {
      console.error('扶養家族データ更新エラー:', error);
      setPayrollState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '扶養家族データの更新に失敗しました'
      }));
    }
  };

  const calculateAnnualTotals = (): AnnualTotals => {
    if (!payrollState.currentSession) {
      return {
        totalGrossSalary: 0,
        totalHealthInsurance: 0,
        totalEmploymentInsurance: 0,
        totalNationalPension: 0,
        totalIncomeTax: 0,
        totalLocalTax: 0,
        totalDefinedContribution: 0,
      };
    }

    return PayrollEstimation.calculateAnnualTotals(payrollState.currentSession.monthlyData, payrollState.currentSession.currentYear);
  };

  const getMonthlyIncomeData = (): MonthlyIncomeData[] => {
    if (!payrollState.currentSession) {
      return [];
    }

    return payrollState.currentSession.monthlyData.map(data => ({
      year: data.year,
      month: data.month,
      isBonus: data.isBonus,
      grossSalary: data.grossSalary || 0,
      healthInsurance: data.healthInsurance || 0,
      employmentInsurance: data.employmentInsurance || 0,
      nationalPension: data.nationalPension || 0,
      definedContribution: data.definedContribution || 0
    }));
  };

  const clearSession = () => {
    PayrollStorage.clearSession();
    setPayrollState({
      currentSession: null,
      isLoading: false,
      error: null
    });
  };

  const setError = (error: string | null) => {
    setPayrollState(prev => ({ ...prev, error }));
  };

  const setLoading = (loading: boolean) => {
    setPayrollState(prev => ({ ...prev, isLoading: loading }));
  };

  // 特定月のデータを推定値にリセット
  const resetMonthlyData = (year: number, month: number, isBonus: boolean) => {
    if (!payrollState.currentSession) {
      throw new Error('セッションが初期化されていません');
    }

    try {
      const updatedMonthlyData = payrollState.currentSession.monthlyData.filter(data =>
        !(data.year === year && data.month === month && data.isBonus === isBonus)
      );

      const updatedSession: PayrollSession = {
        ...payrollState.currentSession,
        monthlyData: updatedMonthlyData,
        lastUpdated: new Date().toISOString()
      };

      // 推定値を再計算
      const sessionWithEstimations = PayrollEstimation.updateEstimations(updatedSession);

      setPayrollState(prev => ({
        ...prev,
        currentSession: sessionWithEstimations,
        error: null
      }));

    } catch (error) {
      console.error('月別データリセットエラー:', error);
      setPayrollState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '月別データのリセットに失敗しました'
      }));
    }
  };


  // ユーザー編集データをすべてリセット（PDF_IMPORTEDは保持）
  const resetAllUserEditedData = () => {
    if (!payrollState.currentSession) {
      throw new Error('セッションが初期化されていません');
    }

    try {
      const filteredData = payrollState.currentSession.monthlyData.filter(data =>
        data.sourceType === DataSourceType.PDF_IMPORTED
      );

      const updatedSession: PayrollSession = {
        ...payrollState.currentSession,
        monthlyData: filteredData,
        lastUpdated: new Date().toISOString()
      };

      // 推定値を再計算
      const sessionWithEstimations = PayrollEstimation.updateEstimations(updatedSession);

      setPayrollState(prev => ({
        ...prev,
        currentSession: sessionWithEstimations,
        error: null
      }));

    } catch (error) {
      console.error('ユーザー編集データリセットエラー:', error);
      setPayrollState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'ユーザー編集データのリセットに失敗しました'
      }));
    }
  };

  // セッションをリストア
  const restoreSession = (restoredSession: PayrollSession) => {
    try {
      const sessionToRestore: PayrollSession = ensureFamilyData({
        ...restoredSession,
        currentYear: restoredSession.currentYear || new Date().getFullYear(),
        sessionId: restoredSession.sessionId || createSessionId(),
        lastUpdated: new Date().toISOString()
      });

      // ローカルストレージに保存
      PayrollStorage.saveSession(sessionToRestore);

      // 状態を更新
      setPayrollState(prev => ({
        ...prev,
        currentSession: sessionToRestore,
        error: null
      }));

    } catch (error) {
      console.error('セッションリストアエラー:', error);
      setPayrollState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'セッションのリストアに失敗しました'
      }));
    }
  };

  // 年度を変更
  const changeYear = (year: number, onCalculationReset?: () => void) => {
    if (!payrollState.currentSession) {
      throw new Error('セッションが初期化されていません');
    }

    try {
      const updatedSession: PayrollSession = {
        ...payrollState.currentSession,
        currentYear: year,
        lastUpdated: new Date().toISOString()
      };

      // 新しい年度の推定値を更新
      const sessionWithEstimations = PayrollEstimation.updateEstimations(updatedSession);

      setPayrollState(prev => ({
        ...prev,
        currentSession: sessionWithEstimations,
        error: null
      }));

      // 計算結果をリセット（コールバックが提供された場合）
      if (onCalculationReset) {
        onCalculationReset();
      }

    } catch (error) {
      console.error('年度変更エラー:', error);
      setPayrollState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '年度の変更に失敗しました'
      }));
    }
  };

  const contextValue: PayrollContextType = {
    payrollState,
    initializeSession,
    addPdfData,
    addBonus,
    deleteBonus,
    updateMonthlyData,
    updateFamilyData,
    calculateAnnualTotals,
    getMonthlyIncomeData,
    clearSession,
    resetMonthlyData,
    resetAllUserEditedData,
    restoreSession,
    setError,
    setLoading,
    changeYear
  };

  return (
    <PayrollContext.Provider value={contextValue}>
      {children}
    </PayrollContext.Provider>
  );
};
